<template>
  <section id="home" class="main-banner">
    <div class="carousel-container" v-if="!loading && banners.length > 0">
      <div 
        v-for="(item, index) in banners" 
        :key="item.id"
        class="slide"
        :class="{ active: index === currentIndex }"
      >
        <img :src="item.url" :alt="'Slide ' + (index + 1)" loading="lazy">
      </div>

      <button class="control prev" @click="prevSlide" aria-label="Slide anterior">‹</button>
      <button class="control next" @click="nextSlide" aria-label="Próximo slide">›</button>

      <div class="indicators">
        <span 
          v-for="(_, index) in banners" 
          :key="index"
          :class="{ active: index === currentIndex }"
          @click="currentIndex = index"
        ></span>
      </div>
    </div>
    
    <div v-else class="banner-placeholder">
      {{ loading ? 'Carregando banners...' : 'Nenhum banner encontrado.' }}
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';


const endpoint = process.env.VUE_APP_HYGRAPH_URL;

interface BannerSlide {
  id: string;
  url: string;
}

const banners = ref<BannerSlide[]>([]);
const loading = ref(true);
const currentIndex = ref(0);
let timer: any = null;

// Função para otimizar a imagem via URL do Hygraph
const optimizeImage = (url: string) => {
  if (!url) return '';
  // Redimensiona para 1600px de largura e ajusta a qualidade para 80% (economiza banda)
  return url.replace('https://media.graphassets.com/', 'https://media.graphassets.com/resize=width:1400/quality=value:80/auto_image/compress/');
};

const fetchBanners = async () => {
  const query = `
    query GetBanners {
      bannerCarrossels {
        id,
        imageFirst{
            id,
            url(transformation: {
            image: { resize: { width: 1200, height: 600, fit: crop } },
            document: { output: { format: webp } }
          })
        } 
      }
    }
  `;

  try {
    const response = await fetch(endpoint!, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query })
    });

    const request = await response.json();

    if (request.data && request.data.bannerCarrossels.length > 0) {
      const rawData = request.data.bannerCarrossels[0].imageFirst;
      
      //console.log(`${JSON.stringify(rawData,null,2)}`);


      const slides: BannerSlide[] = (rawData || [])
      .map((img: any) => ({       
        id: img.id,
        url: optimizeImage(img.url)
      }))
      .filter((slide: BannerSlide) => slide.url && slide.url !== '');

//console.log(`retorno Slides ${JSON.stringify(slides,null,2)}`);

      banners.value = slides;
    }
  } catch (error) {
    console.error("Erro ao carregar banners:", error);
  } finally {
    loading.value = false;
    startTimer();
  }
};

const nextSlide = () => {
  if (banners.value.length > 0) {
    currentIndex.value = (currentIndex.value + 1) % banners.value.length;
  }
};

const prevSlide = () => {
  if (banners.value.length > 0) {
    currentIndex.value = (currentIndex.value - 1 + banners.value.length) % banners.value.length;
  }
};

const startTimer = () => {
  if (banners.value.length > 1) {
    timer = setInterval(nextSlide, 5000);
  }
};

onMounted(fetchBanners);
onUnmounted(() => { if (timer) clearInterval(timer); });
</script>

<style scoped>
@import "@vueup/vue-quill/dist/vue-quill.snow.css";

.prose-custom :deep(span) {
  color: inherit;
}
.main-banner {
  width: 100%;
  height: 700px; /* Sua altura original */
  background-color: #000;
  position: relative;
  overflow: hidden;
}

.carousel-container {
  width: 100%;
  height: 100%;
  position: relative;
}

.slide {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  transition: opacity 0.8s ease-in-out; /* Suaviza a troca */
  display: flex;
  align-items: center;
  justify-content: center;
}

.slide.active {
  opacity: 1;
  z-index: 1;
}

.slide img {
  width: 100%;
  height: 100%;
  /* O PONTO CHAVE: 'cover' garante que a imagem preencha o banner sem deformar */
  object-fit: cover; 
  /* 'center' garante que o foco da imagem seja o meio dela */
  object-position: center; 
  background-color: bg-slate-900;
}

.control {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.4);
  color: white;
  border: none;
  font-size: 2rem;
  padding: 10px 15px;
  cursor: pointer;
  z-index: 10;
  border-radius: 4px;
  transition: background 0.3s;
}

.control:hover { background: rgba(0, 0, 0, 0.7); }
.prev { left: 20px; }
.next { right: 20px; }

.indicators {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 10px;
  z-index: 10;
}

.indicators span {
  width: 12px;
  height: 12px;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 50%;
  cursor: pointer;
}

.indicators span.active { background: #fff; }

.banner-placeholder {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  background: #111;
}

@media (max-width: 768px) {
  .main-banner {
    height: 400px; /* Altura ideal para celulares: não ocupa a tela toda, mas mostra bem a foto */
  }

  .control {
    font-size: 1.2rem; /* Setas menores para não cobrir o conteúdo */
    padding: 8px 12px;
    background: rgba(0, 0, 0, 0.2); /* Mais discreto no toque */
  }

  .prev { left: 10px; }
  .next { right: 10px; }

  .indicators {
    bottom: 10px; /* Sobe um pouco os pontinhos */
  }
}

/* Para celulares muito pequenos */
@media (max-width: 480px) {
  .main-banner {
    height: 300px; /* Altura de "faixa" para telas pequenas */
  }
}
</style>